/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend
} from 'recharts';
import ChartContainer from './ChartContainer';
import GlassTooltip from './GlassTooltip';
import { CHART_COLORS } from './chartConstants';

interface RadarDataPoint {
  metric: string;
  value: number;
  competitor?: number;
  fullMark?: number;
}

interface BrandRadarChartProps {
  data: RadarDataPoint[];
  title?: string;
  showCompetitor?: boolean;
  brandName?: string;
  competitorName?: string;
  className?: string;
}

/**
 * Brand Radar Chart Component - Multi-dimensional brand performance visualization
 */
export const BrandRadarChart: React.FC<BrandRadarChartProps> = ({
  data,
  title = 'Brand Performance',
  showCompetitor = false,
  brandName = 'Your Brand',
  competitorName = 'Competitor',
  className = ''
}) => {
  return (
    <ChartContainer title={title} className={className}>
      <ResponsiveContainer width="100%" height={400}>
        <RadarChart data={data}>
          <PolarGrid stroke="var(--border)" />
          <PolarAngleAxis
            dataKey="metric"
            tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
          />

          <Radar
            name={brandName}
            dataKey="value"
            stroke={CHART_COLORS.primary}
            fill={CHART_COLORS.primary}
            fillOpacity={0.6}
          />

          {showCompetitor && (
            <Radar
              name={competitorName}
              dataKey="competitor"
              stroke={CHART_COLORS.secondary}
              fill={CHART_COLORS.secondary}
              fillOpacity={0.4}
            />
          )}

          <GlassTooltip />
          <Legend
            wrapperStyle={{
              paddingTop: '20px'
            }}
            iconType="circle"
          />
        </RadarChart>
      </ResponsiveContainer>

      {/* Key insights */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        {data.slice(0, 4).map((item, idx) => (
          <div
            key={idx}
            className="p-3 rounded-lg"
            style={{ background: 'var(--background-secondary)' }}
          >
            <div className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
              {item.metric}
            </div>
            <div className="flex items-baseline justify-between">
              <div className="text-lg font-bold" style={{ color: 'var(--foreground)' }}>
                {item.value}
              </div>
              {showCompetitor && item.competitor !== undefined && (
                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  vs {item.competitor}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </ChartContainer>
  );
};
